import { Link } from 'react-router-dom';
import { SiteFooter } from '@/components/_vs/marketing/SiteFooter';
import { BrandLogo } from '@/components/_vs/marketing/BrandLogo';
import { VisualyaMark } from '@/components/_vs/marketing/VisualyaMark';
import { LogoBrace } from '@/components/_vs/marketing/LogoBrace';
import { getSocialIcon } from '@/components/_vs/marketing/SocialIcons';
import { isDarkColor } from '@/components/_vs/marketing/colorContrast';
import { useI18n } from '@/components/_vs/i18n/I18nProvider';
import { useConsent } from '@/components/_vs/consent/ConsentProvider';
import { DEFAULT_LOCALE, localizedPath } from '@/i18n/config';
import { resolveLocalizedTree } from '@/utils/localize';
import settings from '@/content/settings.json';

export function Footer({ locale = DEFAULT_LOCALE }) {
  const { t } = useI18n();
  const { openPreferences } = useConsent();
  const footer = resolveLocalizedTree(settings.footer, locale);
  const year = new Date().getFullYear();
  return (
    <SiteFooter
      logo={
        <BrandLogo
          settings={footer}
          fallbackText="Morettino Bottega"
          dark={isDarkColor(footer.bgColor)}
          height={footer.logoHeight ?? 40}
          maxWidth={footer.logoMaxWidth || undefined}
          opacity={footer.logoOpacity ?? 50}
        />
      }
      tagline={footer.tagline}
      addressLine={
        footer.addressLine && (
          <span className="inline-flex items-center gap-1.5">
            <LogoBrace side="left" className="h-7 w-auto" />
            {footer.addressLine}
            <LogoBrace side="right" className="h-7 w-auto" />
          </span>
        )
      }
      copyright={`© ${year}${footer.copyrightText ? ' ' + footer.copyrightText : ''}`}
      bgColor={footer.bgColor}
      bottomBgColor={footer.bottomBgColor}
      signatureBgColor={footer.signatureBgColor}
      legalUppercase={footer.legalUppercase}
      menuUppercase={footer.menuUppercase}
      linkAs={Link}
      columns={(footer.menus || [])
        .filter((m) => m.enabled)
        .map((m) => ({ title: m.title, links: m.links }))}
      // legalLinks punta sempre a uno slug di pagina interno (Privacy/Termini/
      // Cookie), mai a un URL esterno — a differenza di menus/socials può
      // essere passato per localizedPath in sicurezza, altrimenti su /en il
      // link "Terms" riportava alla versione italiana della pagina.
      bottomLinks={(footer.legalLinks || []).map((l) => ({ ...l, href: localizedPath(l.href, locale) }))}
      socialLinks={(footer.socials || []).map((s) => ({ href: s.href, label: s.platform, icon: getSocialIcon(s.platform) }))}
      rightSlot={
        <button
          onClick={openPreferences}
          // I <button> non ereditano text-transform dagli antenati (reset
          // dello user-agent stylesheet sui form control) — va ripetuto qui
          // esplicitamente per restare coerente col resto della barra quando
          // "Copyright e link legali in maiuscolo" è attivo.
          className={'transition-colors hover:text-theme-text text-[11px] tracking-wide' + (footer.legalUppercase ? ' uppercase' : '')}
        >
          {t('footer.cookiePreferences')}
        </button>
      }
      signature={
        footer.showVisualyaSignature !== false && (
          <a
            href="https://visualya.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex flex-col items-center gap-2 text-theme-text-subtle transition-colors hover:text-theme-text"
          >
            <VisualyaMark className="h-3.5 w-auto text-theme-text-subtle transition-colors group-hover:text-white" />
            <span className="text-[7px] font-normal uppercase tracking-[0.3em]">
              Made in <span className="font-bold">Visualya</span>
            </span>
          </a>
        )
      }
    />
  );
}
